import React, { useState, useEffect, useCallback } from 'react';
import { cldThumb } from '../utils/img';
import { listRiderReports, reviewRiderReport, fixFreeShoeBarcode, confirmAllFreeShoes, adminAddReturns } from '../api/riderReports';

const PAGE_SIZE = 50;
const STATUSES = [
  { key: 'pending', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'rejected', label: 'Rejected' },
];

const KINDS = {
  returns: {
    title: 'Rider Returns',
    blurb: 'Garments customers sent back, as reported by riders. Confirming sends the item to the Restock queue.',
    confirmLabel: 'Confirm return',
    confirmEffect: 'moves to the Restock queue',
  },
  'free-shoes': {
    title: 'Rider Free Shoes',
    blurb: 'Free pairs riders handed over against an appointment. Confirming marks the item gifted (no revenue line).',
    confirmLabel: 'Confirm free pair',
    confirmEffect: 'is marked gifted',
  },
};

const fmtTime = iso => iso
  ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '';

const pill = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-gray-100 text-gray-500 border-gray-200',
};

function ReviewModal({ kind, row, action, onClose, onDone }) {
  const cfg = KINDS[kind];
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const confirming = action === 'confirm';

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await reviewRiderReport(kind, row.id, action, note);
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
        <h3 className="text-base font-bold text-gray-800 mb-2">
          {confirming ? cfg.confirmLabel : 'Reject report'}
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          {confirming
            ? <>Item <b>{row.item?.zoddle_barcode}</b> (now <b>{row.item?.status}</b>) {cfg.confirmEffect}.</>
            : <>The report is closed and the item is left as it is.</>}
        </p>
        <textarea
          value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Note (optional)"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:border-zoddle-green"
        />
        {error && <p className="text-sm text-zoddle-pink mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600">Cancel</button>
          <button
            onClick={submit} disabled={saving}
            className={`px-4 py-1.5 text-sm rounded-lg font-semibold text-white disabled:opacity-50 ${confirming ? 'bg-zoddle-green' : 'bg-zoddle-pink'}`}
          >
            {saving ? 'Saving…' : confirming ? 'Confirm' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BarcodeFixer({ row, onDone }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const save = async () => {
    setError(null);
    try { await fixFreeShoeBarcode(row.id, value.trim()); onDone(); }
    catch (e) { setError(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="mt-1">
      <div className="flex gap-1">
        <input
          value={value} onChange={e => setValue(e.target.value)} placeholder="Correct barcode"
          className="border border-gray-200 rounded px-2 py-1 text-xs w-32 focus:outline-none focus:border-zoddle-green"
        />
        <button onClick={save} disabled={!value.trim()} className="text-xs px-2 py-1 rounded bg-zoddle-blue text-white disabled:opacity-40">Match</button>
      </div>
      {error && <p className="text-xs text-zoddle-pink mt-1">{error}</p>}
    </div>
  );
}

function ConfirmAllShoes({ pendingCount, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    if (!window.confirm(`Confirm all ${pendingCount} pending free pairs? Each item is marked gifted.`)) return;
    setBusy(true); setError(null); setResult(null);
    try { setResult(await confirmAllFreeShoes()); onDone(); }
    catch (e) { setError(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
      <button
        onClick={run} disabled={busy || pendingCount === 0}
        className="px-4 py-1.5 text-sm rounded-lg font-semibold bg-zoddle-green text-white disabled:opacity-40"
      >Confirm all ({pendingCount})</button>
      {error && <p className="text-sm text-zoddle-pink mt-2">{error}</p>}
      {result && (
        <div className="mt-2 text-sm">
          <p className="text-gray-700">Gifted <b>{result.confirmed}</b>{result.failed.length > 0 && <>, skipped <b>{result.failed.length}</b></>}.</p>
          {result.failed.map((f, i) => (
            <p key={i} className="text-xs text-zoddle-pink">{f.barcode || '�'}: {f.reason}</p>
          ))}
        </div>
      )}
    </div>
  );
}

const REASONS = [
  ['SIZE_ISSUE', 'Size issue'], ['DEFECT', 'Defect'], ['DESIGN_NOT_LIKED', 'Design not liked'],
  ['CHANGED_MIND', 'Changed mind'], ['OTHER', 'Other'],
];

function AddReturn({ onDone }) {
  const [open, setOpen] = useState(false);
  const [uid, setUid] = useState('');
  const [codes, setCodes] = useState('');
  const [reason, setReason] = useState('CHANGED_MIND');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState(null);   // result of the dry-run check

  const payload = dry => ({
    appointment_uid: uid.trim(), barcodes: codes.split(/[\s,]+/).filter(Boolean),
    reason_code: reason, reason_text: text || null, dry_run: dry,
  });
  const edit = setter => e => { setter(e.target.value); setPreview(null); setMsg(null); };

  const check = async () => {
    if (!uid.trim() || payload(true).barcodes.length === 0) { setMsg({ err: 'Enter the appointment UID and at least one barcode' }); return; }
    setBusy(true); setMsg(null);
    try { setPreview(await adminAddReturns(payload(true))); }
    catch (e) { setPreview(null); setMsg({ err: e.response?.data?.detail || 'Check failed' }); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await adminAddReturns(payload(false));
      setMsg({ ok: `${r.added} item(s) returned and sent to the Restock queue` });
      setPreview(null); setCodes(''); setText('');
      onDone();
    } catch (e) { setMsg({ err: e.response?.data?.detail || 'Failed' }); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="mb-4 px-3 py-1.5 text-sm rounded bg-zoddle-green text-white">
        + Add return
      </button>
    );
  }
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 space-y-3">
      <div className="flex justify-between">
        <h3 className="text-sm font-bold text-gray-800">Add a return</h3>
        <button onClick={() => setOpen(false)} className="text-xs text-gray-400">Close</button>
      </div>
      <input value={uid} onChange={edit(setUid)} placeholder="Appointment UID"
        className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
      <textarea value={codes} onChange={edit(setCodes)} rows={2}
        placeholder="Item barcodes (comma, space or new line separated)"
        className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
      <div className="flex gap-2">
        <select value={reason} onChange={edit(setReason)} className="border border-gray-200 rounded px-2 py-2 text-sm">
          {REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={text} onChange={edit(setText)}
          placeholder={reason === 'OTHER' ? 'Note (required)' : 'Note (optional)'}
          className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm" />
      </div>
      {msg?.err && <p className="text-sm text-zoddle-pink">{msg.err}</p>}
      {msg?.ok && <p className="text-sm text-green-700">{msg.ok}</p>}

      {preview && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500">
            Appointment {preview.appointment_uid} - {preview.ok
              ? 'all items check out. Confirming sends them to the Restock queue.'
              : 'fix the items marked in red before you can confirm.'}
          </div>
          {preview.checks.map(c => (
            <div key={c.barcode} className="flex gap-3 items-center px-3 py-2 border-t border-gray-100 text-sm">
              {c.item?.image_url
                ? <img src={cldThumb(c.item.image_url, 96)} alt="" className="w-10 h-10 rounded object-cover" />
                : <div className="w-10 h-10 rounded bg-gray-100" />}
              <div className="flex-1">
                <div className="font-semibold text-gray-800">{c.barcode}</div>
                <div className="text-xs text-gray-500">
                  {c.item ? `${c.item.brand || ''} - ${c.item.design_name || ''} - ${c.item.size || ''}` : ''}
                </div>
              </div>
              <div className="text-xs text-gray-500">{c.order_id ? `Order ${c.order_id}` : ''}</div>
              <div className={`text-xs font-semibold ${c.problem ? 'text-zoddle-pink' : 'text-green-700'}`}>
                {c.problem || 'OK'}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={check} disabled={busy} className="px-3 py-1.5 text-sm rounded border border-zoddle-green text-zoddle-green disabled:opacity-40">
          {busy && !preview ? 'Checking...' : 'Check'}
        </button>
        {preview?.ok && (
          <button onClick={confirm} disabled={busy} className="px-3 py-1.5 text-sm rounded bg-zoddle-green text-white disabled:opacity-40">
            Confirm return ({preview.checks.length})
          </button>
        )}
      </div>
    </div>
  );
}

function RiderReviewScreen({ kind }) {
  const cfg = KINDS[kind];
  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ rows: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);   // { row, action }

  const load = useCallback(() => {
    setLoading(true); setError(null);
    listRiderReports(kind, status, page * PAGE_SIZE, PAGE_SIZE)
      .then(setData)
      .catch(e => setError(e.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [kind, status, page]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="p-6">
      <h2 className="text-lg font-bold text-gray-800">{cfg.title}</h2>
      <p className="text-sm text-gray-500 mb-4">{cfg.blurb}</p>

      <div className="flex gap-2 mb-4">
        {STATUSES.map(s => (
          <button
            key={s.key}
            onClick={() => { setStatus(s.key); setPage(0); }}
            className={`px-3 py-1 rounded-full text-sm border ${
              status === s.key ? 'bg-zoddle-green text-white border-zoddle-green' : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            {s.label} ({data.counts?.[s.key] ?? 0})
          </button>
        ))}
      </div>

      {kind === 'free-shoes' && <ConfirmAllShoes pendingCount={data.counts?.pending ?? 0} onDone={load} />}
      {kind === 'returns' && <AddReturn onDone={load} />}

      {error && <p className="text-sm text-zoddle-pink mb-3">{error}</p>}
      {loading &&<p className="text-sm text-gray-400 mb-3">Loading…</p>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2">Item</th>
              <th className="px-3 py-2">{kind === 'returns' ? 'Order / Appointment' : 'Appointment'}</th>
              {kind === 'returns' && <th className="px-3 py-2">Reason</th>}
              <th className="px-3 py-2">Item status</th>
              <th className="px-3 py-2">Reported</th>
              <th className="px-3 py-2">Review</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(row => (
              <tr key={row.id} className="border-t border-gray-100 align-top">
                <td className="px-3 py-2">
                  <div className="flex gap-2">
                    {row.item?.image_url
                      ? <img src={cldThumb(row.item.image_url, 96)} alt="" className="w-12 h-12 rounded object-cover" />
                      : <div className="w-12 h-12 rounded bg-gray-100" />}
                    <div>
                      <div className="font-semibold text-gray-800">{row.item?.zoddle_barcode || row.barcode}</div>
                      <div className="text-xs text-gray-500">
                        {row.item ? `${row.item.brand || ''} · ${row.item.design_name || ''}` : null}
                      </div>
                      <div className="text-xs text-gray-400">{row.item?.size}</div>
                      {kind === 'free-shoes' && !row.barcode_matched && (
                        <>
                          <span className="text-xs font-semibold text-zoddle-pink">Barcode not found: {row.barcode}</span>
                          {row.review_status === 'pending' && <BarcodeFixer row={row} onDone={load} />}
                        </>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {kind === 'returns' ? (
                    <>
                      <div>{row.source_order_id}</div>
                      {row.source_appointment_id
                        ? <div className="text-gray-400">{row.source_appointment_id}</div>
                        : <div className="text-zoddle-pink font-semibold">Order not found</div>}
                    </>
                  ) : (
                    <div>{row.source_appointment_id}</div>
                  )}
                </td>
                {kind === 'returns' && (
                  <td className="px-3 py-2 text-xs text-gray-600">
                    <div className="font-semibold">{row.reason_code}</div>
                    {row.reason_text && <div className="text-gray-400">{row.reason_text}</div>}
                  </td>
                )}
                <td className="px-3 py-2 text-xs">{row.item?.status || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{fmtTime(row.created_at)}</td>
                <td className="px-3 py-2">
                  {row.review_status === 'pending' ? (
                    <div className="flex gap-1">
                      <button
                        onClick={() => setModal({ row, action: 'confirm' })}
                        disabled={!row.item}
                        className="px-2 py-1 text-xs rounded bg-zoddle-green text-white disabled:opacity-40"
                      >Confirm</button>
                      <button
                        onClick={() => setModal({ row, action: 'reject' })}
                        className="px-2 py-1 text-xs rounded border border-zoddle-pink text-zoddle-pink"
                      >Reject</button>
                    </div>
                  ) : (
                    <div>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${pill[row.review_status]}`}>{row.review_status}</span>
                      <div className="text-xs text-gray-400 mt-1">{row.reviewed_by} {fmtTime(row.reviewed_at)}</div>
                      {row.review_note && <div className="text-xs text-gray-500">{row.review_note}</div>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Nothing here.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-3 mt-3 text-sm">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Previous</button>
          <span className="text-gray-500">Page {page + 1} of {pages}</span>
          <button disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
        </div>
      )}

      {modal && (
        <ReviewModal
          kind={kind} row={modal.row} action={modal.action}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

export const RiderReturns = () => <RiderReviewScreen kind="returns" />;
export const RiderFreeShoes = () => <RiderReviewScreen kind="free-shoes" />;
